import { useCallback, useMemo, useState, type FormEventHandler } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import TextInput from 'components/Common/Form/Input/TextInput'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import HelpPane from 'components/Common/HelpPane'
import OtpResendHelp from 'components/Common/Otp/OtpResendHelp'
import type { OtpDeliveryChannel } from 'constants/otpDelivery'
import { formatPhoneNumber, sanitisePhoneNumber } from 'lib/format/formatPhoneNumber'
import { PENDING_ACTIONS, type PendingAction } from '../constants'
import type { PendingSignUpOTPData } from '../types'
import { ErrorBanner, PendingButtonContent, renderStrongText, StepBackButton, Title } from './shared'

interface Props {
  pendingSignUpOTPData: PendingSignUpOTPData
  autoFocus: boolean
  error: any
  processing: boolean
  countDown: number
  resendLabel: string
  defaultContact?: App.GeoContact
  onBack: () => void
  onSubmit: FormEventHandler<HTMLFormElement>
  onResend: () => void
  onRequestCall: () => void
  isActionPending: (action: PendingAction) => boolean
}

function JoinOtpStep({
  pendingSignUpOTPData,
  autoFocus,
  error,
  processing,
  countDown,
  resendLabel,
  defaultContact,
  onBack,
  onSubmit,
  onResend,
  onRequestCall,
  isActionPending,
}: Props) {
  const intl = useIntl()
  const [lastChannel, setLastChannel] = useState<OtpDeliveryChannel>('sms')
  const hasServerError = error?.status === 500
  const formattedPhone = formatPhoneNumber(
    sanitisePhoneNumber(pendingSignUpOTPData.phone ?? ''),
    pendingSignUpOTPData.phonePrefix ?? '',
  )
  const codeSentMessage = useMemo(() => (
    lastChannel === 'call' ? (
      <FormattedMessage
        id="If77TFQEPm6pGjWI9wkc"
        defaultMessage="We’re calling <strong>{phone}</strong> with a 6-digit code. The code is valid for 10 minutes and after that you will need to request a new one."
        description="Instructions text explaining OTP code is being delivered by voice call during sign-up"
        values={{
          phone: formattedPhone,
          strong: renderStrongText,
        }}
      />
    ) : (
      <FormattedMessage
        id="yV-vkLCKjc25W0f4Ro87"
        defaultMessage="We’ve sent a 6-digit code to <strong>{phone}</strong>. The code is valid for 10 minutes and after that you will need to request a new one."
        description="Instructions text explaining OTP code was sent during sign-up"
        values={{
          phone: formattedPhone,
          strong: renderStrongText,
        }}
      />
    )
  ), [formattedPhone, lastChannel])
  const didntReceiveText = useMemo(() => intl.formatMessage({
    id: '5qw5gXcFCNH0-GV2_0xh',
    defaultMessage: 'Didn’t receive the SMS?',
    description: 'Help text asking if user received the SMS during sign-up OTP',
  }), [intl])
  const handleResendSms = useCallback(() => {
    setLastChannel('sms')
    onResend()
  }, [onResend])
  const handleRequestCall = useCallback(() => {
    setLastChannel('call')
    onRequestCall()
  }, [onRequestCall])

  return (
    <>
      <StepBackButton
        label={intl.formatMessage({
          id: '_I-T4-bL3TaYsTZhGL9E',
          defaultMessage: 'Back',
          description: 'Back button label for embedded account access chat widget',
        })}
        onClick={onBack}
      />
      <Title>{intl.formatMessage({
        id: 'h4BGXfuNA3QpJTNM8hLX',
        defaultMessage: 'Verification code',
        description: 'Modal title for sign-up OTP verification step in the flow',
      })}
      </Title>
      <VerticalSpacer as="form" gap={16} onSubmit={onSubmit}>
        <ErrorBanner
          error={error}
          genericMessage={intl.formatMessage({
            id: 'CokbDsgDRwzfq9y6UHm0',
            defaultMessage: 'Something went wrong. Please try again later.',
            description: 'Generic server error message during sign-up OTP verification',
          })}
        />
        <BodyText variant="medium">
          {codeSentMessage}
        </BodyText>
        <TextInput
          type="text"
          required
          id="embedded-join-code"
          name="code"
          label={intl.formatMessage({
            id: 'iaU-d0VAUYtsuJuyCTtu',
            defaultMessage: '6-digit code',
            description: 'Form field label for OTP code input field during sign-up',
          })}
          placeholder={intl.formatMessage({
            id: 'c1wFMggNHMa4pcCzPs-z',
            defaultMessage: 'Enter 6-digit code',
            description: 'Input placeholder text for OTP code input during sign-up',
          })}
          requiredErrorMessage={intl.formatMessage({
            id: 'mXmn0i3aOyPRhZzQSh3R',
            defaultMessage: 'Code is required',
            description: 'Validation error when OTP code field is empty during sign-up',
          })}
          minLength={6}
          maxLength={6}
          pattern="^[0-9]*$"
          invalidErrorMessage={intl.formatMessage({
            id: '_bpgjmEo7iW-zZ0qiEEx',
            defaultMessage: 'Your code must be 6 digits long',
            description: 'Validation error when OTP code is not 6 digits during sign-up',
          })}
          inputMode="numeric"
          autoFocus={autoFocus}
        />
        {!hasServerError && (
          <TextButton kind="primary" shape="round" size="medium" fit="flex" type="submit" disabled={processing}>
            <PendingButtonContent
              pending={isActionPending(PENDING_ACTIONS.submitSignUpOtp)}
              label={intl.formatMessage({
                id: 'DWnCJGSLzfdtKZnuV2SZ',
                defaultMessage: 'Submit',
                description: 'Submit button text for OTP verification during sign-up',
              })}
            />
          </TextButton>
        )}
      </VerticalSpacer>
      {!hasServerError && (
        <OtpResendHelp
          countDown={countDown}
          prompt={didntReceiveText}
          resendLabel={resendLabel}
          onResendSms={handleResendSms}
          onRequestCall={handleRequestCall}
        />
      )}
      {hasServerError && defaultContact && <HelpPane contactNumber={defaultContact} />}
    </>
  )
}

export default JoinOtpStep
